import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from '../modals/DefaultModal';
import useProgramaSituacao from '../hooks/useProgramaSituacao';

const SubAcaoModal = ({ show, onHide, subacao, onSuccess }) => {
  const initialFormData = {
    id: '',
    idPrograma: '',
    idAcao: '',
    cnpjAssociacao: localStorage.getItem('cnpj'),
    descricao: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [programas, setProgramas] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const { isProgramaAtivo } = useProgramaSituacao();
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const programasRes = await axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
          params: { cnpjAssociacao: cnpj },
          headers: { Authorization: `Bearer ${token}` },
        });

        setProgramas(programasRes.data);

        if (subacao?.idPrograma) {
          await loadAcoes(subacao.idPrograma);
        } else {
          setFormData(initialFormData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (show) {
      fetchInitialData();
    } else {
      setFormData(initialFormData);
      setAcoes([]);
    }
  }, [show, subacao]);

  // Aplica subacao somente após ações estarem carregadas
  useEffect(() => {
    if (subacao && acoes.length > 0) {
      const acaoExiste = acoes.some(a => a.id === subacao.idAcao);
      if (acaoExiste) {
        setFormData({ ...subacao });
      } else {
        console.warn('idAcao não corresponde a nenhuma ação carregada.');
        setFormData({ ...subacao, idAcao: '' });
      }
    }
  }, [acoes, subacao]);

  useEffect(() => {
    if (formData.idPrograma) {
      const programaAtivo = isProgramaAtivo(formData.idPrograma);
      setIsProgramaInativo(!programaAtivo);
    } else {
      setIsProgramaInativo(false);
    }
  }, [formData.idPrograma, isProgramaAtivo]);

  const loadAcoes = async idPrograma => {
    try {
      const response = await axios.get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAcoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar ações:', error);
      setAcoes([]);
    }
  };

  const handleChange = async e => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'descricao') {
      processedValue = value.toUpperCase();
    } else if (['idPrograma', 'idAcao'].includes(name)) {
      processedValue = value ? parseInt(value, 10) : '';
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    if (name === 'idPrograma') {
      setAcoes([]);
      setFormData(prev => ({ ...prev, idAcao: '' }));
      await loadAcoes(value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.idPrograma && !isProgramaAtivo(formData.idPrograma)) {
      showDefaultModal('error', 'Este programa está ENCERRADO. Não é possível salvar Sub-Ações para programas encerrados.');
      return;
    }

    const payload = {
      ...formData,
      cnpjAssociacao: cnpj,
      ano: anoBase,
    };

    try {
      const url = `${API_BASE_URL.SUB_ACAO}/save`;
      await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showDefaultModal('success', 'Sub-Ação salva com sucesso!', () => {
        onSuccess();
        onHide();
      });
    } catch (error) {
      console.error('Erro ao salvar sub-ação:', error);
      const errorMessage =
        error.response?.data?.message ||
        'Erro ao salvar a sub-ação. Tente novamente.';
      showDefaultModal('error', errorMessage);
    }
  };

  if (!show) return null;
  if (isLoading)
    return <div className="text-center p-3">Carregando dados...</div>;

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div
            className="modal-header bg-success text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              {formData.id ? 'Editar Sub-Ação' : 'Nova Sub-Ação'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onHide}
              aria-label="Close"
            />
          </div>

          {isProgramaInativo && (
            <div className="alert alert-warning mb-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Este programa está <strong>ENCERRADO</strong>. Você pode visualizar os dados, mas não é possível salvar alterações.
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              <div className="row g-3">
                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Programa</label>
                  <select
                    name="idPrograma"
                    className="form-select"
                    value={formData.idPrograma || ''}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {programas.map(programa => (
                      <option key={programa.id} value={programa.id}>
                        {programa.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Ação</label>
                  <select
                    name="idAcao"
                    className="form-select"
                    value={formData.idAcao || ''}
                    onChange={handleChange}
                    disabled={!formData.idPrograma}
                    required
                  >
                    <option value="">Selecione...</option>
                    {acoes.map(acao => (
                      <option key={acao.id} value={acao.id}>
                        {acao.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-12 input-group-sm">
                  <label className="form-label">Nome da Sub-Ação</label>
                  <input
                    onChange={handleChange}
                    value={formData.descricao || ''}
                    name="descricao"
                    type="text"
                    className="form-control text-uppercase"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onHide}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-success btn-sm" disabled={isProgramaInativo} title={isProgramaInativo ? 'Programa ENCERRADO - não é possível salvar' : undefined}>
                {formData.id ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </div>
  );
};

export default SubAcaoModal;
